import { useState, useEffect, useMemo, useCallback } from 'react';
import type { ContextMessage } from '../../../shared/types/context';
import { api } from '../../lib/api';
import { useSelectionStore } from '../../state/selectionStore';
import { TextContentViewer } from '../TextContentViewer';

interface TextContentBlock {
  type: 'text';
  text: string;
}

function isSimpleTextContent(parsed: unknown): parsed is [TextContentBlock] {
  if (!Array.isArray(parsed) || parsed.length !== 1) {
    return false;
  }
  const block = parsed[0];
  return (
    block &&
    typeof block === 'object' &&
    'type' in block &&
    block.type === 'text' &&
    'text' in block &&
    typeof block.text === 'string'
  );
}

function formatMessageContent(message: ContextMessage | null): { displayContent: string; editContent: string; isSimpleText: boolean } {
  if (!message?.content) {
    return { displayContent: '', editContent: '', isSimpleText: false };
  }

  try {
    // Try to parse as JSON
    const parsed = JSON.parse(message.content);

    // Check if it's the simple text content block pattern
    if (isSimpleTextContent(parsed)) {
      return {
        displayContent: JSON.stringify(parsed, null, 2), // Always show full JSON for viewing
        editContent: parsed[0].text, // Edit just the text value
        isSimpleText: true,
      };
    }

    // Otherwise, pretty-print the full JSON
    const formatted = JSON.stringify(parsed, null, 2);
    return {
      displayContent: formatted,
      editContent: formatted,
      isSimpleText: false,
    };
  } catch {
    // If not JSON, return as-is
    return {
      displayContent: message.content,
      editContent: message.content,
      isSimpleText: false,
    };
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function highlightJSON(content: string): string {
  // First, escape all HTML to prevent injection
  const escaped = escapeHtml(content);

  // Then apply syntax highlighting on the safe HTML-encoded content
  return escaped
    .replace(/(&quot;(?:[^&]|&(?!quot;))*&quot;)\s*:/g, '<span class="hl-property">$1</span>:')
    .replace(/:\s*(&quot;(?:[^&]|&(?!quot;))*&quot;)/g, ': <span class="hl-string">$1</span>')
    .replace(/:\s*(true|false|null)/g, ': <span class="hl-keyword">$1</span>')
    .replace(/:\s*(-?\d+\.?\d*)/g, ': <span class="hl-literal">$1</span>');
}

export function MessageDetailPanel() {
  const [currentMessage, setCurrentMessage] = useState<ContextMessage | null>(null);
  const [isSimpleText, setIsSimpleText] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const messageId = useSelectionStore((state) => state.message_id);
  const select = useSelectionStore((state) => state.set);

  useEffect(() => {
    let cancelled = false;

    const loadMessage = async () => {
      if (!messageId) {
        setCurrentMessage(null);
        return;
      }

      try {
        const message = await api().services.context.getMessage(messageId);
        if (!cancelled) {
          setCurrentMessage(message);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[MessageDetailPanel] Failed to load message:', error);
          setCurrentMessage(null);
        }
      }
    };

    loadMessage();

    return () => {
      cancelled = true;
    };
  }, [messageId]);

  useEffect(() => {
    let cancelled = false;

    const loadSystemPrompt = async () => {
      try {
        const prompt = await api().getSystemPrompt();
        if (!cancelled) {
          setSystemPrompt(prompt);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[MessageDetailPanel] Failed to load system prompt:', error);
        }
      }
    };

    loadSystemPrompt();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleForkAndRun = async () => {
    if (!currentMessage?.id) return;
    const forkedSession = await api().services.context.forkSession(currentMessage.id);

    // Switch to the new session
    select({ session_id: forkedSession.id });

    // Get the last message from the forked session
    const lastMessage = forkedSession.messages?.[forkedSession.messages.length - 1];
    if (lastMessage) {
      select({ message_id: lastMessage.id });
      await api().agent.run(lastMessage.id);
    } else {
      select({ message_id: null });
    }
  };

  const handleFork = async () => {
    if (!currentMessage?.id) return;
    const forkedSession = await api().services.context.forkSession(currentMessage.id);

    // Switch to the new session
    select({ session_id: forkedSession.id });

    // Select the last message in the forked session
    const lastMessage = forkedSession.messages?.[forkedSession.messages.length - 1];
    if (lastMessage) {
      select({ message_id: lastMessage.id });
    } else {
      select({ message_id: null });
    }
  };

  const handleRevert = async () => {
    if (!currentMessage?.id) return;
    const result = await api().services.context.revertMessages(currentMessage.id);
    if ('error' in result) {
      console.error('[MessageDetailPanel] Failed to revert messages:', result.error);
    }
  };

  const handleRun = async () => {
    if (!currentMessage?.id) return;
    await api().agent.run(currentMessage.id);
  };

  const handleCheckout = async () => {
    if (!currentMessage?.version_id) return;
    try {
      await api().services.files.checkout(currentMessage.version_id);
      select({ version_id: currentMessage.version_id });
    } catch (error) {
      console.error('[MessageDetailPanel] Failed to checkout version:', error);
    }
  };

  const handleHost = async () => {
    if (!currentMessage?.version_id) return;
    try {
      await api().services.meta.set('host_version_id', currentMessage.version_id);
      console.log(`[MessageDetailPanel] Set host_version_id to ${currentMessage.version_id}`);
      select({ version_id: currentMessage.version_id });
    } catch (error) {
      console.error('[MessageDetailPanel] Failed to set host version:', error);
    }
  };

  const handleActivate = async () => {
    if (!currentMessage?.id) return;
    try {
      const agentId = await api().services.meta.get('active_agent_id')
      const agent = await api().services.agent.update(agentId, {last_message_id: currentMessage.id})
      console.log(`[MessageDetailPanel] Set active agent last_message_id to ${currentMessage.id}`);
    } catch (error) {
      console.error('[MessageDetailPanel] Failed to activate message:', error);
    }
  };

  const handleSnapshot = async () => {
    if (!currentMessage?.id) return;
    try {
      const result = await api().services.files.snapshot();
      const versionId = result.versionId;
      await api().services.files.updateVersion({id: versionId, major: 1});

      const updated = await api().services.context.updateMessage({
        id: currentMessage.id,
        version_id: versionId,
      });

      if (updated) {
        setCurrentMessage(updated);
        select({ version_id: versionId });
      }

      console.log(`[MessageDetailPanel] Created snapshot ${versionId} and updated message ${currentMessage.id}`);
    } catch (error) {
      console.error('[MessageDetailPanel] Failed to snapshot:', error);
    }
  };

  const handleUpdateSystemPrompt = async () => {
    if (!currentMessage?.id || currentMessage.role !== 'system') return;
    try {
      const textBlock: TextContentBlock = { type: 'text', text: systemPrompt };
      const contentToSave = JSON.stringify([textBlock]);

      const updated = await api().services.context.updateMessage({
        id: currentMessage.id,
        content: contentToSave,
      });
      if (updated) {
        setCurrentMessage(updated);
      }
    } catch (error) {
      console.error('[MessageDetailPanel] Failed to update system prompt:', error);
    }
  };

  const handleSave = useCallback(async (newContent: string) => {
    if (!currentMessage?.id) return;
    try {
      // If it's simple text content, reconstruct the JSON structure
      let contentToSave = newContent;
      if (isSimpleText) {
        const textBlock: TextContentBlock = { type: 'text', text: newContent };
        contentToSave = JSON.stringify([textBlock]);
      }

      const updated = await api().services.context.updateMessage({
        id: currentMessage.id,
        content: contentToSave,
      });
      if (updated) {
        setCurrentMessage(updated);
      }
    } catch (error) {
      console.error('[MessageDetailPanel] Failed to save message:', error);
      throw error;
    }
  }, [currentMessage?.id, isSimpleText]);

  const renderContent = useCallback((content: string) => {
    return highlightJSON(content);
  }, []);

  const formatted = useMemo(() => formatMessageContent(currentMessage), [currentMessage]);

  // Update isSimpleText state when formatted content changes
  useEffect(() => {
    setIsSimpleText(formatted.isSimpleText);
  }, [formatted.isSimpleText]);

  // Check if current message is a system message and if it matches the current system prompt
  const shouldShowUpdateButton = useMemo(() => {
    if (!currentMessage || currentMessage.role !== 'system' || !systemPrompt) {
      return false;
    }

    // Extract text from message content
    try {
      const parsed = JSON.parse(currentMessage.content);
      if (isSimpleTextContent(parsed)) {
        return parsed[0].text !== systemPrompt;
      }
    } catch {
      // If not JSON, compare directly
      return currentMessage.content !== systemPrompt;
    }

    return false;
  }, [currentMessage, systemPrompt]);

  if (!currentMessage) {
    return (
      <div className="empty-state">
        <p>Select a message to inspect its details.</p>
      </div>
    );
  }

  return (
    <div className="panel-shell">
      <div className="panel-controls">
        <button onClick={handleForkAndRun} className="btn">Fork & Run</button>
        <button onClick={handleFork} className="btn">Fork</button>
        <button onClick={handleRevert} className="btn">Revert</button>
        <button onClick={handleRun} className="btn">Run</button>
        <button onClick={handleActivate} className="btn">Activate</button>
        <button onClick={handleSnapshot} className="btn">Snapshot</button>
        {currentMessage.version_id && (
          <>
            <button onClick={handleHost} className="btn">Host</button>
            <button onClick={handleCheckout} className="btn">Checkout</button>
          </>
        )}
        {shouldShowUpdateButton && (
          <button onClick={handleUpdateSystemPrompt} className="btn btn-warning">
            Update
          </button>
        )}
      </div>
      <div className="message-metadata">
        <span className="message-metadata-key">id:</span> {currentMessage.id}
        <span className="message-metadata-separator">·</span>
        <span className="message-metadata-key">session_id:</span> {currentMessage.session_id}
        <span className="message-metadata-separator">·</span>
        <span className="message-metadata-key">role:</span> {currentMessage.role}
        <span className="message-metadata-separator">·</span>
        <span className="message-metadata-key">version_id:</span> {currentMessage.version_id ?? 'NULL'}
        <span className="message-metadata-separator">·</span>
        <span className="message-metadata-key">response_id:</span> {currentMessage.response_id ?? 'NULL'}
        <span className="message-metadata-separator">·</span>
        <span className="message-metadata-key">timestamp:</span> {new Date(currentMessage.timestamp).toISOString()}
      </div>
      <div className="panel-content">
        <TextContentViewer
          content={formatted.displayContent}
          editContent={formatted.editContent}
          onSave={handleSave}
          renderContent={renderContent}
        />
      </div>
    </div>
  );
}
